/**
 * CSV Import/Export Utilities
 * Uses AppState field names directly (no ProjectState conversion needed)
 */

import type { AppState } from '../store/app-state';
import type { Scene, Row, Prop } from '../types';
import type { Personnel } from '../features/personnel/types';
import { getPersonnelProjectTitle, type PersonnelProjectTitleOptions } from '../features/personnel/utils/characterDisplay';
import type { SoundCue } from '../features/sound/types';
import type { CostumePiece } from '../features/costumes/types';
import type { ScheduleEvent, DeadlineEvent } from '../features/scheduler/types';
import type { Character } from '../features/show-structure/types';
import type { LightingCue, FollowSpotCue, FollowSpot } from '../features/lighting/types';

/**
 * State subset needed for CSV operations
 */
interface CSVState {
    projectMeta: AppState['projectMeta'];
    showStructure: AppState['showStructure'];
    rows: Row[];
    props: Prop[];
}

/**
 * Flattens the state into a CSV string
 * @param state - Full AppState or CSVState subset 
 * @returns CSV content
 */
export function exportToCSV(state: CSVState): string {
    const rows = [];

    // ===== METADATA SECTION =====
    rows.push(["--- METADATA ---"]);
    rows.push([`"Show Title"`, `"${state.projectMeta?.title || ''}"`]);
    rows.push([`"Director"`, `"${state.projectMeta?.director || ''}"`]);
    rows.push([`"SM"`, `"${state.projectMeta?.sm || ''}"`]);
    rows.push([`"ASM"`, `"${state.projectMeta?.asm || ''}"`]);

    rows.push([]);

    // ===== SCENES SECTION =====
    rows.push(["--- SCENES ---"]);
    const headers = ["Act", "Scene Name", "Row #", "Moment", "Type", "Item", "Where", "Who", "Notes"];
    rows.push(headers.map(h => `"${h}"`));

    for (const { actName, sceneName, row } of getActiveSceneRows(state)) {
        rows.push([
            `"${actName}"`,
            `"${sceneName}"`,
            `"${row.number}"`,
            `"${row.moment}"`,
            `"${row.type}"`,
            `"${row.item}"`,
            `"${row.location}"`,
            `"${row.who}"`,
            `"${row.notes}"`
        ]);
    }

    // ===== PROPS SECTION =====
    rows.push([]);
    rows.push(["--- PROPS ---"]);
    const propsHeaders = ["Prop Name", "Act Name", "Scene Name", "Entrance", "Exit", "Notes"];
    rows.push(propsHeaders.map(h => `"${h}"`));

    for (const { prop, sceneInfo } of getActivePropRows(state)) {
        rows.push([
            `"${prop.name || ''}"`,
            `"${sceneInfo.actName}"`,
            `"${sceneInfo.sceneName}"`,
            `"${prop.entrance || ''}"`,
            `"${prop.exit || ''}"`,
            `"${prop.notes || ''}"`
        ]);
    }

    return rows.map(r => r.join(",")).join("\n");
}


export function downloadCSV(csvContent: string, filename: string) {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const anchor = document.createElement("a");
    if (anchor.download !== undefined) {
        const url = URL.createObjectURL(blob);
        anchor.href = url;
        anchor.download = filename;
        anchor.style.visibility = 'hidden';
        document.body.appendChild(anchor);
        try {
            anchor.click();
        } finally {
            anchor.remove();
            URL.revokeObjectURL(url);
        }
    }
}

// ===== HELPER FUNCTIONS =====

// Unique ID generator to avoid duplicates in tight loops
const genHelperId = (prefix: string) => `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

/**
 * Properly escape a value for CSV (handle quotes, commas, newlines)
 */
function stringifyCSVValue(value: unknown): string {
    if (value == null) return '""';
    if (typeof value === 'string') return value;
    if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') {
        return String(value);
    }
    if (value instanceof Date) {
        return value.toISOString();
    }

    const serialized = JSON.stringify(value);
    return serialized ?? '';
}

function escapeCSV(value: unknown) {
    const str = stringifyCSVValue(value);
    if (str.includes(',') || str.includes('"') || str.includes('\n') || str.includes('\r')) {
        return `"${str.replace(/"/g, '""')}"`;
    }
    return `"${str}"`;
}

/**
 * CSV splitting ignoring commas inside quotes (shared helper)
 */
function splitCSV(str: string) {
    const res = [];
    let current = '';
    let inQuotes = false;
    for (const char of str) {
        if (char === '"') {
            inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
            res.push(current);
            current = '';
        } else {
            current += char;
        }
    }
    res.push(current);
    return res.map(s => s.trim().replace(/^"|"$/g, '').replace(/""/g, '"'));
}

/**
 * Normalize type values to valid options
 */
function normalizeType(type: string | undefined): Row['type'] {
    const normalized = (type || '').toUpperCase().trim();
    const valid = ['SET', 'STRIKE', 'MOVE', 'NULL'];
    return valid.includes(normalized) ? (normalized as Row['type']) : 'NULL';
}

interface SceneOrderedCue {
    sceneId?: string | null;
    sortOrder?: number | null;
}

interface SceneLabel {
    actName: string;
    sceneName: string;
}

function buildProvidedSceneOrderIndex(scenes: { id: string }[]): Map<string, number> {
    return new Map(scenes.map((scene, index) => [scene.id, index]));
}

function compareByProvidedSceneOrder<T extends SceneOrderedCue>(
    sceneOrderIndex: Map<string, number>,
    left: T,
    right: T
): number {
    const leftSceneRank = getProvidedSceneOrder(sceneOrderIndex, left.sceneId);
    const rightSceneRank = getProvidedSceneOrder(sceneOrderIndex, right.sceneId);
    if (leftSceneRank !== rightSceneRank) {
        return leftSceneRank - rightSceneRank;
    }

    return (left.sortOrder ?? 0) - (right.sortOrder ?? 0);
}

function getProvidedSceneOrder(
    sceneOrderIndex: Map<string, number>,
    sceneId: string | null | undefined
): number {
    if (!sceneId) return Number.POSITIVE_INFINITY;
    return sceneOrderIndex.get(sceneId) ?? Number.POSITIVE_INFINITY;
}

function getCSVDate(): string {
    return new Date().toISOString().slice(0, 10);
}

function getActiveSceneRows(state: CSVState): { actName: string; sceneName: string; row: Row }[] {
    const rows: { actName: string; sceneName: string; row: Row }[] = [];
    const structActs = state.showStructure.acts;
    const structScenes = state.showStructure.scenes;

    for (const act of structActs) {
        if (act.deleted) continue;

        for (const sceneId of act.sceneIds) {
            const scene = structScenes.find(s => s.id === sceneId);
            if (!scene || scene.deleted) continue;

            const sceneRows = state.rows ? state.rows.filter(r => r.sceneId === scene.id && !r.deleted) : [];
            for (const row of sceneRows) {
                rows.push({ actName: act.name, sceneName: scene.name, row });
            }
        }
    }

    return rows;
}

function buildActiveSceneLabelMap(showStructure: CSVState['showStructure']): Record<string, SceneLabel> {
    const sceneMap: Record<string, SceneLabel> = {};

    for (const act of showStructure.acts) {
        if (act.deleted) continue;
        for (const sceneId of act.sceneIds) {
            const scene = showStructure.scenes.find(s => s.id === sceneId);
            if (scene && !scene.deleted) {
                sceneMap[scene.id] = { actName: act.name, sceneName: scene.name };
            }
        }
    }

    return sceneMap;
}

function getActivePropRows(state: CSVState): { prop: Prop; sceneInfo: SceneLabel }[] {
    const sceneMap = buildActiveSceneLabelMap(state.showStructure);
    const propRows: { prop: Prop; sceneInfo: SceneLabel }[] = [];

    for (const prop of state.props ?? []) {
        if (prop.deleted) continue;
        propRows.push({
            prop,
            sceneInfo: sceneMap[prop.sceneId || ''] || { actName: '', sceneName: '' },
        });
    }

    return propRows;
}

function getActiveSceneOrderedCues<T extends SceneOrderedCue & { deleted?: boolean }>(
    cues: T[],
    scenes: { id: string }[]
): T[] {
    const sceneOrderIndex = buildProvidedSceneOrderIndex(scenes);
    return cues
        .filter(cue => !cue.deleted)
        .toSorted((a, b) => compareByProvidedSceneOrder(sceneOrderIndex, a, b));
}

// ===== SEPARATE CSV EXPORT FUNCTIONS =====

/**
 * Export only scenes data as CSV (no section markers, spreadsheet-friendly)
 */
export function exportScenesCSV(state: CSVState) {
    const rows = [];
    const headers = ["Act", "Scene Name", "Row #", "Moment", "Type", "Item", "Where", "Who", "Notes"];
    rows.push(headers.map(h => escapeCSV(h)).join(','));

    for (const { actName, sceneName, row } of getActiveSceneRows(state)) {
        rows.push([
            escapeCSV(actName),
            escapeCSV(sceneName),
            escapeCSV(row.number),
            escapeCSV(row.moment),
            escapeCSV(row.type),
            escapeCSV(row.item),
            escapeCSV(row.location),
            escapeCSV(row.who),
            escapeCSV(row.notes)
        ].join(','));
    }

    return rows.join('\n');
}

/**
 * Export only props data as CSV (no section markers, spreadsheet-friendly)
 */
export function exportPropsCSV(state: CSVState) {
    const rows = [];
    const headers = ["Prop Name", "Act Name", "Scene Name", "Entrance", "Exit", "Notes"];
    rows.push(headers.map(h => escapeCSV(h)).join(','));

    for (const { prop, sceneInfo } of getActivePropRows(state)) {
        rows.push([
            escapeCSV(prop.name),
            escapeCSV(sceneInfo.actName),
            escapeCSV(sceneInfo.sceneName),
            escapeCSV(prop.entrance),
            escapeCSV(prop.exit),
            escapeCSV(prop.notes)
        ].join(','));
    }

    return rows.join('\n');
}

// ===== PER-TAB CSV EXPORT FUNCTIONS =====

/**
 * Export personnel/contacts as CSV
 */
export function buildContactsCSV(
    personnel: Personnel[],
    options: PersonnelProjectTitleOptions = {},
): string {
    const rows: string[] = [];
    const headers = ['Name', 'Category', 'Role/Title', 'Department', 'Email', 'Phone', 'Emergency Contact', 'Emergency Phone', 'Notes'];
    rows.push(headers.map(h => escapeCSV(h)).join(','));

    for (const p of personnel.filter(p => !p.deleted)) {
        rows.push([
            escapeCSV(p.name),
            escapeCSV(p.category),
            escapeCSV(getPersonnelProjectTitle(p, options)),
            escapeCSV(p.department || ''),
            escapeCSV(p.email || ''),
            escapeCSV(p.phone || ''),
            escapeCSV(p.emergencyName || ''),
            escapeCSV(p.emergencyPhone || p.emergency || ''),
            escapeCSV(p.notes || ''),
        ].join(','));
    }

    return rows.join('\n');
}

export function exportContactsCSV(
    personnel: Personnel[],
    _projectTitle: string,
    options: PersonnelProjectTitleOptions = {},
): void {
    downloadCSV(buildContactsCSV(personnel, options), `on-book-contacts-${getCSVDate()}.csv`);
}

/**
 * Export schedule events as CSV (excludes deadlines — those have their own export)
 */
export function buildScheduleCSV(events: ScheduleEvent[]): string {
    const rows: string[] = [];
    const headers = ['Date', 'Start', 'End', 'Type', 'Title', 'Location'];
    rows.push(headers.map(h => escapeCSV(h)).join(','));

    for (const e of events
        .filter(e => e.type !== 'deadline')
        .toSorted((a, b) => a.date.localeCompare(b.date) || a.startTime.localeCompare(b.startTime))) {
        rows.push([
            escapeCSV(e.date),
            escapeCSV(e.startTime),
            escapeCSV(e.endTime),
            escapeCSV(e.type),
            escapeCSV(e.title),
            escapeCSV(e.location || ''),
        ].join(','));
    }

    return rows.join('\n');
}

export function exportScheduleCSV(
    events: ScheduleEvent[],
    _projectTitle: string
): void {
    downloadCSV(buildScheduleCSV(events), `on-book-schedule-${getCSVDate()}.csv`);
}

/**
 * Export production deadlines as CSV, optionally filtered by department
 */
export function buildDeadlinesCSV(
    events: ScheduleEvent[],
    personnel: Personnel[],
    departmentId?: string
): string {
    const rows: string[] = [];
    const headers = ['Title', 'Department', 'Assigned To', 'Due Date', 'Status'];
    rows.push(headers.map(h => escapeCSV(h)).join(','));

    let deadlines = events.filter((e): e is DeadlineEvent => e.type === 'deadline');
    if (departmentId) {
        deadlines = deadlines.filter(d => d.linkedDepartment === departmentId);
    }

    for (const d of deadlines.toSorted((a, b) => a.date.localeCompare(b.date))) {
        const assignee = d.assignedTo
            ? personnel.find(p => p.id === d.assignedTo)?.name || ''
            : '';
        const isOverdue = d.status === 'pending' && d.date < getCSVDate();
        const statusLabel = d.status === 'pending'
            ? (isOverdue ? 'OVERDUE' : 'Pending')
            : d.status === 'met' ? 'Met' : 'Missed';

        rows.push([
            escapeCSV(d.title),
            escapeCSV(d.linkedDepartment),
            escapeCSV(assignee),
            escapeCSV(d.date),
            escapeCSV(statusLabel),
        ].join(','));
    }

    return rows.join('\n');
}

export function exportDeadlinesCSV(
    events: ScheduleEvent[],
    personnel: Personnel[],
    departmentId?: string
): void {
    downloadCSV(buildDeadlinesCSV(events, personnel, departmentId), `on-book-deadlines-${getCSVDate()}.csv`);
}

/**
 * Export costume pieces as CSV
 */
export function buildCostumesCSV(
    pieces: CostumePiece[],
    characters: Character[],
    scenes: { id: string; name: string }[]
): string {
    const rows: string[] = [];
    const headers = ['Description', 'Character', 'Scene', 'Status', 'Quick Change', 'Notes'];
    rows.push(headers.map(h => escapeCSV(h)).join(','));

    for (const p of pieces.filter(p => !p.deleted)) {
        const character = characters.find(c => c.id === p.characterId);
        const scene = scenes.find(s => s.id === p.sceneId);

        rows.push([
            escapeCSV(p.description),
            escapeCSV(character?.name || ''),
            escapeCSV(scene?.name || ''),
            escapeCSV(p.status),
            escapeCSV(p.quickChange?.isQuickChange ? 'Yes' : 'No'),
            escapeCSV(p.notes),
        ].join(','));
    }

    return rows.join('\n');
}

export function exportCostumesCSV(
    pieces: CostumePiece[],
    characters: Character[],
    scenes: { id: string; name: string }[]
): void {
    downloadCSV(buildCostumesCSV(pieces, characters, scenes), `on-book-costumes-${getCSVDate()}.csv`);
}

/**
 * Export sound cues as CSV
 */
export function buildSoundCuesCSV(
    cues: SoundCue[],
    scenes: { id: string; name: string }[]
): string {
    const rows: string[] = [];
    const headers = ['Cue Number', 'Description', 'Scene', 'Cue Line', 'Notes'];
    rows.push(headers.map(h => escapeCSV(h)).join(','));
    for (const c of getActiveSceneOrderedCues(cues, scenes)) {
        const scene = scenes.find(s => s.id === c.sceneId);
        rows.push([
            escapeCSV(c.cueNumber),
            escapeCSV(c.description),
            escapeCSV(scene?.name || ''),
            escapeCSV(c.cueLine),
            escapeCSV(c.notes || ''),
        ].join(','));
    }

    return rows.join('\n');
}

export function exportSoundCuesCSV(
    cues: SoundCue[],
    scenes: { id: string; name: string }[]
): void {
    downloadCSV(buildSoundCuesCSV(cues, scenes), `on-book-sound-cues-${getCSVDate()}.csv`);
}

/**
 * Export lighting cues as CSV
 */
export function buildLightingCuesCSV(
    cues: LightingCue[],
    scenes: { id: string; name: string }[]
): string {
    const rows: string[] = [];
    const headers = ['Cue Number', 'Scene', 'Description', 'Cue Line', 'Notes'];
    rows.push(headers.map(h => escapeCSV(h)).join(','));

    const sceneMap = Object.fromEntries(scenes.map(s => [s.id, s.name]));
    for (const c of getActiveSceneOrderedCues(cues, scenes)) {
        rows.push([
            escapeCSV(c.cueNumber),
            escapeCSV(sceneMap[c.sceneId] || ''),
            escapeCSV(c.description),
            escapeCSV(c.cueLine),
            escapeCSV(c.notes || ''),
        ].join(','));
    }

    return rows.join('\n');
}

export function exportLightingCuesCSV(
    cues: LightingCue[],
    scenes: { id: string; name: string }[]
): void {
    downloadCSV(buildLightingCuesCSV(cues, scenes), `on-book-lighting-cues-${getCSVDate()}.csv`);
}

/**
 * Export follow spot cues as CSV
 */
export function buildFollowSpotCuesCSV(
    cues: FollowSpotCue[],
    spots: FollowSpot[],
    characters: { id: string; name: string }[],
    scenes: { id: string; name: string }[]
): string {
    const rows: string[] = [];
    const headers = ['Cue Number', 'Spot', 'Character', 'Action', 'Frame', 'Size', 'Intensity', 'Scene', 'Notes'];
    rows.push(headers.map(h => escapeCSV(h)).join(','));

    const spotMap = Object.fromEntries(spots.map(s => [s.id, s.label]));
    const charMap = Object.fromEntries(characters.map(c => [c.id, c.name]));
    const sceneMap = Object.fromEntries(scenes.map(s => [s.id, s.name]));
    for (const c of getActiveSceneOrderedCues(cues, scenes)) {
        rows.push([
            escapeCSV(c.cueNumber),
            escapeCSV(spotMap[c.spotId] || ''),
            escapeCSV(charMap[c.characterId] || ''),
            escapeCSV(c.action),
            escapeCSV(c.frame),
            escapeCSV(c.size),
            escapeCSV(c.intensity != null ? String(c.intensity) : ''),
            escapeCSV(sceneMap[c.sceneId] || ''),
            escapeCSV(c.notes || ''),
        ].join(','));
    }

    return rows.join('\n');
}

export function exportFollowSpotCuesCSV(
    cues: FollowSpotCue[],
    spots: FollowSpot[],
    characters: { id: string; name: string }[],
    scenes: { id: string; name: string }[]
): void {
    downloadCSV(buildFollowSpotCuesCSV(cues, spots, characters, scenes), `on-book-follow-spot-cues-${getCSVDate()}.csv`);
}

// ===== SEPARATE CSV IMPORT FUNCTIONS =====

/**
 * Import scenes-only CSV (no section markers expected)
 * Modifies existingState in place
 */
export function importScenesCSV(csvContent: string, existingState: CSVState, append = false) {
    if (!existingState.rows) existingState.rows = [];
    const lines = csvContent.split(/\r?\n/).filter(l => l.trim() !== '');
    if (lines.length < 2) return existingState.showStructure.acts; // Header only

    const structActs = existingState.showStructure.acts;
    const structScenes = existingState.showStructure.scenes;

    const genIdLocal = (prefix: string) => `${prefix}_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;

    if (!append) {
        // Clear existing (maintain Preset)
        const presetAct = structActs.find(a => a.isPreset);
        structActs.length = 0; 
        if (presetAct) structActs.push(presetAct);
        
        structScenes.length = 0;
        existingState.rows.length = 0;
    }

    // Skip header row (index 0)
    for (let i = 1; i < lines.length; i++) {
        const cols = splitCSV(lines[i]!);
        if (cols.length < 6) continue;

        const [actName = '', sceneName = '', rowNum = '', moment = '', type = '', item = '', where = '', who = '', notes = ''] = cols;

        // Find or create Act
        let act = structActs.find(a => a.name === actName);
        if (act) {
            if (act.deleted) act.deleted = false; 
        } else {
            act = { id: genIdLocal('act'), name: actName, isPreset: false, sceneIds: [] };
            structActs.push(act);
        }

        // Find or create Scene
        const sceneId = act.sceneIds.find(sid => {
            const s = structScenes.find(ss => ss.id === sid);
            return s && s.name === sceneName;
        });

        let scene: Scene;
        if (sceneId) {
            scene = structScenes.find(s => s.id === sceneId)!;
            if (scene.deleted) scene.deleted = false;
        } else {
            scene = { id: genIdLocal('scene'), name: sceneName, linkedSetId: null, notes: '' };
            structScenes.push(scene);
            act.sceneIds.push(scene.id);
        }

        // Add Row (Flat)
        existingState.rows.push({
            id: genIdLocal('row'),
            sceneId: scene.id,
            number: rowNum || '',
            moment: moment || '',
            type: normalizeType(type),
            item: item || '',
            location: where || '',
            who: who || '',
            notes: notes || '',
            isDeckCue: false,
        });
    }

    return existingState.showStructure.acts;
}

/**
 * Import props-only CSV (no section markers expected)
 * Now with smart header detection - columns can be in any order!
 */
export function importPropsCSV(csvContent: string, existingState: CSVState, append = false) {
    const lines = csvContent.split(/\r?\n/).filter(l => l.trim() !== '');
    if (lines.length < 2) return append ? existingState.props : []; // Header only

    const props = append ? [...existingState.props] : [];

    // Build scene lookup from current state
    const sceneLookup: Record<string, Record<string, string>> = {};
    const structActs = existingState.showStructure.acts;
    const structScenes = existingState.showStructure.scenes;

    for (const act of structActs) {
        if (!sceneLookup[act.name]) sceneLookup[act.name] = {};
        for (const sid of act.sceneIds) {
            const scene = structScenes.find(s => s.id === sid);
            if (scene) {
                (sceneLookup[act.name] ??= {})[scene.name] = scene.id;
            }
        }
    }

    // Parse header row and create column index mapping
    const headerCols = splitCSV(lines[0]!);
    const columnMap: Record<string, number> = {};
    
    // Map known column names (case-insensitive) to their indices
    const knownColumns: Record<string, string> = {
        'prop name': 'name',
        'prop': 'name',
        'name': 'name',
        'act name': 'actName',
        'act': 'actName',
        'scene name': 'sceneName',
        'scene': 'sceneName',
        'entrance': 'entrance',
        'exit': 'exit',
        'notes': 'notes',
        'note': 'notes'
    };
    
    for (const [index, col] of headerCols.entries()) {
        const normalizedCol = col.toLowerCase().trim();
        if (knownColumns[normalizedCol]) {
            columnMap[knownColumns[normalizedCol]] = index;
        }
    }

    // Skip header row (index 0), process data rows
    for (let i = 1; i < lines.length; i++) {
        const cols = splitCSV(lines[i]!);
        if (cols.length < 1 || cols.every(c => c.trim() === '')) continue; // Skip empty rows

        // Extract values using the column map (undefined if column doesn't exist)
        const name = columnMap.name !== undefined ? (cols[columnMap.name] ?? '') : '';
        const actName = columnMap.actName !== undefined ? (cols[columnMap.actName] ?? '') : '';
        const sceneName = columnMap.sceneName !== undefined ? (cols[columnMap.sceneName] ?? '') : '';
        const entrance = columnMap.entrance !== undefined ? (cols[columnMap.entrance] ?? '') : '';
        const exit = columnMap.exit !== undefined ? (cols[columnMap.exit] ?? '') : '';
        const notes = columnMap.notes !== undefined ? (cols[columnMap.notes] ?? '') : '';

        // Skip rows without a prop name
        if (!name || name.trim() === '') continue;

        // Look up scene ID
        let sceneId = '';
        if (actName && sceneName && sceneLookup[actName]?.[sceneName]) {
            sceneId = sceneLookup[actName][sceneName];
        }

        props.push({
            id: genHelperId('prop'),
            name: name.trim(),
            type: 'Hand',
            sceneId: sceneId,
            entrance: entrance.trim(),
            exit: exit.trim(),
            notes: notes.trim(),
            status: 'undefined',
            images: [],
            budget: { currency: 'USD' },
            maintenance: {
                needsRepair: false,
                repairNotes: '',
                needsLaundry: false,
                maintenanceNotes: '',
            },
            source: {
                source: 'stock',
            },
        });
    }

    return props;
}

// ===== DEADLINE CSV IMPORT =====

/** Department alias map for lenient matching */
const DEPARTMENT_ALIASES: Record<string, string> = {
    'costumes': 'costumes',
    'costume': 'costumes',
    'wardrobe': 'costumes',
    'props': 'props',
    'properties': 'props',
    'sets': 'sets',
    'scenic': 'sets',
    'scenery': 'sets',
    'lighting': 'lighting',
    'lights': 'lighting',
    'electrics': 'lighting',
    'sound': 'sound',
    'audio': 'sound',
    'stage-management': 'stage-management',
    'stage management': 'stage-management',
    'sm': 'stage-management',
    'general': 'general',
};

/** Parsed row from CSV before store insertion */
export interface ParsedDeadlineRow {
    title: string;
    date: string;           // normalized to YYYY-MM-DD
    department: string;     // resolved DepartmentId
    assignedToName: string; // original CSV name
    assignedToId: string | null; // matched personnel ID or null
    status: 'pending' | 'met' | 'missed';
}

/** Result of importDeadlinesCSV */
export interface DeadlineImportResult {
    rows: ParsedDeadlineRow[];
    /** Person names that auto-matched */
    matchedNames: { csvName: string; personnelId: string; personnelName: string }[];
    /** Person names that could not be auto-matched */
    unmatchedNames: string[];
    /** Department values that defaulted to 'general' */
    departmentWarnings: string[];
    /** Row indices (0-based data rows) that were skipped due to missing required fields */
    skippedRows: number[];
}

/**
 * Parse a deadlines CSV string into structured rows with matching metadata.
 * Pure function — does not modify the store.
 */
export function importDeadlinesCSV(
    csvContent: string,
    personnel: Personnel[]
): DeadlineImportResult {
    const lines = csvContent.split(/\r?\n/).filter(l => l.trim() !== '');
    const result: DeadlineImportResult = {
        rows: [],
        matchedNames: [],
        unmatchedNames: [],
        departmentWarnings: [],
        skippedRows: [],
    };

    if (lines.length < 2) return result; // Header only or empty

    // ── Smart header detection ──
    const headerCols = splitCSV(lines[0]!);
    const columnMap: Record<string, number> = {};

    const knownHeaders: Record<string, string> = {
        'title': 'title',
        'deadline': 'title',
        'deadline title': 'title',
        'name': 'title',
        'due date': 'date',
        'date': 'date',
        'due': 'date',
        'department': 'department',
        'dept': 'department',
        'assigned to': 'assignedTo',
        'assigned': 'assignedTo',
        'assignee': 'assignedTo',
        'status': 'status',
    };

    for (const [index, col] of headerCols.entries()) {
        const normalized = col.toLowerCase().trim();
        if (knownHeaders[normalized]) {
            columnMap[knownHeaders[normalized]] = index;
        }
    }

    // Must have at least title and date columns
    if (columnMap.title === undefined || columnMap.date === undefined) {
        return result;
    }

    // Build personnel lookup (case-insensitive)
    const personnelByName = new Map<string, Personnel>();
    for (const p of personnel.filter(p => !p.deleted)) {
        personnelByName.set(p.name.toLowerCase().trim(), p);
    }

    // Track unique unmatched names and department warnings
    const seenUnmatched = new Set<string>();
    const seenDeptWarnings = new Set<string>();
    const seenMatched = new Map<string, { personnelId: string; personnelName: string }>();

    // ── Parse data rows ──
    for (let i = 1; i < lines.length; i++) {
        const cols = splitCSV(lines[i]!);
        if (cols.every(c => c.trim() === '')) continue; // skip blank rows

        const title = (columnMap.title !== undefined ? (cols[columnMap.title] ?? '') : '').trim();
        const rawDate = (columnMap.date !== undefined ? (cols[columnMap.date] ?? '') : '').trim();
        const rawDept = (columnMap.department !== undefined ? (cols[columnMap.department] ?? '') : '').trim();
        const rawAssigned = (columnMap.assignedTo !== undefined ? (cols[columnMap.assignedTo] ?? '') : '').trim();
        const rawStatus = (columnMap.status !== undefined ? (cols[columnMap.status] ?? '') : '').trim();

        // Required fields
        if (!title || !rawDate) {
            result.skippedRows.push(i - 1);
            continue;
        }

        // Date normalization: detect MM/DD/YYYY and convert to YYYY-MM-DD
        let date = rawDate;
        const slashMatch = rawDate.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
        if (slashMatch) {
            const [, month, day, year] = slashMatch;
            date = `${year!}-${month!.padStart(2, '0')}-${day!.padStart(2, '0')}`;
        }
        // Validate YYYY-MM-DD
        if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
            result.skippedRows.push(i - 1);
            continue;
        }

        // Department matching
        let department = 'general';
        if (rawDept) {
            const deptLower = rawDept.toLowerCase().trim();
            const resolved = DEPARTMENT_ALIASES[deptLower];
            if (resolved) {
                department = resolved;
            } else {
                department = 'general';
                if (!seenDeptWarnings.has(rawDept)) {
                    seenDeptWarnings.add(rawDept);
                    result.departmentWarnings.push(rawDept);
                }
            }
        }

        // Person name matching
        let assignedToId: string | null = null;
        if (rawAssigned) {
            const matched = personnelByName.get(rawAssigned.toLowerCase().trim());
            if (matched) {
                assignedToId = matched.id;
                if (!seenMatched.has(rawAssigned.toLowerCase().trim())) {
                    seenMatched.set(rawAssigned.toLowerCase().trim(), {
                        personnelId: matched.id,
                        personnelName: matched.name,
                    });
                    result.matchedNames.push({
                        csvName: rawAssigned,
                        personnelId: matched.id,
                        personnelName: matched.name,
                    });
                }
            } else if (!seenUnmatched.has(rawAssigned.toLowerCase().trim())) {
                    seenUnmatched.add(rawAssigned.toLowerCase().trim());
                    result.unmatchedNames.push(rawAssigned);
                }
        }

        // Status normalization
        let status: 'pending' | 'met' | 'missed' = 'pending';
        if (rawStatus) {
            const statusLower = rawStatus.toLowerCase().trim();
            if (statusLower === 'met') status = 'met';
            else if (statusLower === 'missed') status = 'missed';
            // 'pending', 'overdue', or anything else → 'pending'
        }

        result.rows.push({
            title,
            date,
            department,
            assignedToName: rawAssigned,
            assignedToId,
            status,
        });
    }

    return result;
}

// ===== CSV TEMPLATES =====

export const SCENES_CSV_TEMPLATE = `Act,Scene Name,Row #,Moment,Type,Item,Where,Who,Notes
Act 1,Scene 1,1,Start of scene,SET,Table,Center Stage,Stage Hands,Pre-set
Act 1,Scene 1,2,Enter Alice,MOVE,Chair,SR,Alice,Carries chair on`;

export const PROPS_CSV_TEMPLATE = `Prop Name,Act Name,Scene Name,Entrance,Exit,Notes
Blue Book,Act 1,Scene 1,SR,SL,Carried by Alice
Vintage Lamp,Act 1,Scene 1,Pre-set,Strike,On table`;

export const DEADLINES_CSV_TEMPLATE = `Title,Due Date,Department,Assigned To,Status
Costume designs due,2026-03-15,costumes,,Pending
Props list finalized,2026-03-20,props,,Pending
Set build complete,2026-04-01,sets,,Pending`;
